import React from 'react';
import { useQuery } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';

import { TUnknownObject } from '@common/types/common';

import svgStarIcon from '@common/assets/svg/icon-star.svg';

import userInterfaceStore from '@store/user-interface';
import { TRootState } from '@store/root-reducer';
import providerApi from '@utils/api';
import { buildAddressLine } from '@utils/common';

import InstitutionExpertiseSC, {
  InstitutionExpertiseRowSC,
} from './institution-expertise.styled';
import HearthBeatLoader from '@common/components/loaders/heart-beat/hb-loader';
import StepButtons from '@components/step-buttons';

export type TInstitutionExpertiseRowProps = {
  name: string;
  address: string;
  selected: boolean;
  tag: string;
  available: boolean;
  onSelect: () => void;
};

const InstitutionExpertiseRow: React.FC<TInstitutionExpertiseRowProps> = ({
  name,
  address,
  selected,
  tag,
  available,
  onSelect,
}: TInstitutionExpertiseRowProps) => {
  return (
    <InstitutionExpertiseRowSC selected={selected} onClick={onSelect}>
      <span className="mi-name">{name}</span>
      <span>{address}</span>
      <div className="footer">
        <span className="expertise-tag">{tag}</span>
        {available && (
          <div>
            <img src={svgStarIcon} alt="start" />
            <span className="available-slots">voľné termíny</span>
          </div>
        )}
      </div>
    </InstitutionExpertiseRowSC>
  );
};

const InstitutionExpertise = () => {
  const dispatch = useDispatch();

  const expert = useSelector(
    (store: TRootState) => store.userInterface.order.expert,
  );

  const { id: expertId, name: expertName } = expert;

  const medicalInstitutionId = useSelector(
    (store: TRootState) => store.userInterface.order.medicalInstitution.id,
  );

  const expertiseId = useSelector(
    (store: TRootState) => store.userInterface.order.expertise.id,
  );

  const { isLoading, data } = useQuery(['experts', expertId], async () => {
    const result = await providerApi.custom(
      `/e-calendar/e-visit/workspaces?expertId=${expertId}`,
      {
        method: 'GET',
      },
    );
    return result.data;
  });

  const handleSelect = (
    mId: string,
    mName: string,
    mAddress: string,
    eId: string,
    eName: string,
  ) => {
    dispatch(
      userInterfaceStore.selectMedicalInstitution({
        medicalInstitution: { id: mId, name: mName, address: mAddress },
        expertise: { id: eId, name: eName },
      }),
    );
    dispatch(userInterfaceStore.moveToNextStep());
  };

  return (
    <InstitutionExpertiseSC>
      <div className={'header'}>
        {!isLoading &&
        data &&
        Array.isArray(data.offices) &&
        data.offices.length > 1 ? (
          <h4>Vyberte pracovisko</h4>
        ) : (
          !isLoading && <h4>Pracovisko</h4>
        )}
        <span>{expertName}</span>
      </div>
      <div className="content">
        <div className="list">
          {isLoading && (
            <div className="loading">
              <HearthBeatLoader />
            </div>
          )}
          {!isLoading &&
            data &&
            Array.isArray(data) &&
            data.length > 0 &&
            data.map((item: TUnknownObject, idx: number) => {
              const {
                address: street,
                city,
                postCode,
              } = item;

              const address = buildAddressLine(street, city, postCode);
              console.log('IT', item)
              return (
                <InstitutionExpertiseRow
                  key={idx}
                  selected={
                    medicalInstitutionId === item.id &&
                    (expertiseId == null || expertiseId === item.expertise?.code)
                  }
                  name={item.name}
                  address={address}
                  tag={item.expertise?.name ?? ''}
                  available={item.hasAvailableEVisits}
                  onSelect={() =>
                    handleSelect(
                      item.id,
                      item.name,
                      address,
                      item.expertise?.code ?? '',
                      item.expertise?.name ?? '',
                    )
                  }
                />
              );
            })}
        </div>
      </div>
      <StepButtons />
    </InstitutionExpertiseSC>
  );
};

export default InstitutionExpertise;
